import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.citycourier.org/api/v1';

const apiClient = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
    },
});

apiClient.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('token');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

export type DashboardPreset =
    | 'last_7_days'
    | 'last_30_days'
    | 'last_6_months'
    | 'last_1_year'
    | 'custom';

export interface DashboardSummaryRequest {
    preset?: DashboardPreset;
    from?: string;
    to?: string;
}

export interface DashboardDelta {
    value: number;
    isPositive: boolean;
}

export interface DashboardKpi {
    id: string;
    label: string;
    value: number;
    unit?: 'PKR' | 'shipments' | 'days' | 'percentage';
    delta?: DashboardDelta;
}

export interface ShipmentsTrendPoint {
    date: string;
    booked: number;
    inTransit: number;
    delivered: number;
    returned: number;
}

export interface RevenueTrendPoint {
    date: string;
    revenue: number;
    cod: number;
}

export interface StatusBreakdownSlice {
    status: string;
    count: number;
}

export interface TopListItem {
    name: string;
    shipments: number;
    revenue: number;
}

export interface DeliveryPerformanceSummary {
    onTimePercentage: number;
    latePercentage: number;
    failedDeliveries: number;
    averageDeliveryTimeDays: number;
}

export interface DashboardSummaryResponse {
    filters: {
        preset: DashboardPreset;
        fromDate: string;
        toDate: string;
    };
    kpis: DashboardKpi[];
    shipmentsTrend: ShipmentsTrendPoint[];
    revenueTrend: RevenueTrendPoint[];
    statusBreakdown: StatusBreakdownSlice[];
    topCities: TopListItem[];
    topClients: TopListItem[];
    deliveryPerformance: DeliveryPerformanceSummary;
    lastUpdated: string;
}

const unwrapResponse = <T>(payload: T | { data: T; success?: boolean }): T => {
    if (payload && typeof payload === 'object' && 'data' in payload) {
        return (payload as any).data;
    }
    return payload as T;
};

export async function getDashboardSummary(params: DashboardSummaryRequest): Promise<DashboardSummaryResponse> {
    try {
        const response = await apiClient.get('/dashboard/summary', { params });
        return unwrapResponse<DashboardSummaryResponse>(response.data);
    } catch (error: any) {
        if (axios.isAxiosError(error) && error.response) {
            const message = (error.response.data as any)?.message || 'Failed to load dashboard summary';
            throw new Error(message);
        }
        throw new Error('An unknown error occurred while loading dashboard summary');
    }
}


